import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const mapMagazine = (item, index, language = "en") => {
  const isArabic = language === "ar";
  return {
    id: item.id || String(index + 1),
    title: stripHtml((isArabic ? (item.title_ar || item.Title_ar || item.titleAr || item.TitleAr) : null) || item.title_en || item.Title_en || item.title || item.Title),
    subtitle: stripHtml((isArabic ? (item.subtitle_ar || item.Subtitle_ar || item.subtitleAr || item.SubtitleAr) : null) || item.subtitle_en || item.Subtitle_en || item.subtitle || item.Subtitle),
    image: getCloudflareImageUrl(item.ImagePath || item.image, "w=520,sharpen=1"),
    pdf: (isArabic ? (item.pdf_ar || item.PDF_ar || item.pdfAr || item.PdfAr) : (item.pdf_en || item.PDF_en || item.pdfEn || item.PdfEn)) || item.pdf || item.PDF,
  };
};

export default function Magazines({ language = "en" }) {
  const isArabic = language === "ar";
  const interactions = useHomeInteractions();
  const [magazines, setMagazines] = useState([]);
  const [selectedMagazine, setSelectedMagazine] = useState(null);

  useEffect(() => {
    document.title = isArabic ? "مجلة تراحم | مؤسسة تراحم الخيرية" : "Tarahum Magazine | Tarahum Charity Foundation";
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadMagazines = async () => {
      try {
        const headers = { Accept: "application/json" };
        const token = getJwtToken();
        if (token) headers.Authorization = `Bearer ${token}`;

        const response = await fetch(getApiUrl("/api/GetTarahumMagazineList.ashx?page=1&pageSize=100"), {
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Magazine request failed with ${response.status}`);

        const result = await response.json();
        const rows = Array.isArray(result?.data) ? result.data : [];
        const mappedRows = rows.map((item, index) => mapMagazine(item, index, language)).filter((item) => item.title);

        if (mappedRows.length) {
          setMagazines(mappedRows);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load Tarahum magazines", error);
        }
      }
    };

    loadMagazines();

    return () => controller.abort();
  }, [language]);

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{isArabic ? "مجلة تراحم" : "Tarahum Magazine"}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to="/">{isArabic ? "الرئيسية" : "home"}</Link></li>
              <li>/</li>
              <li>{isArabic ? "مجلة تراحم" : "Tarahum Magazine"}</li>
            </ul>
          </div>
        </div>
      </section>

      <div className={`page-service-single${isArabic ? " arabic-magazines-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
        <div className="container">
          <div className="magazine-wrap">
            <div className="row">
              {magazines.map((magazine) => (
                <div className="col-md-3" key={magazine.id || magazine.title}>
                  <div className="mag-card">
                    <div className="mag-img">
                      <img src={magazine.image} alt={magazine.title} />
                    </div>
                    <div className="mag-title">
                      {magazine.title}
                      {magazine.subtitle ? <p>{magazine.subtitle}</p> : null}
                    </div>
                    <button type="button" className="mag-btn" onClick={() => setSelectedMagazine(magazine)}>
                      {isArabic ? "تحميل ملف PDF" : "PDF Download"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selectedMagazine ? (
        <div className="mag-pdf-modal" role="dialog" aria-modal="true" aria-label={isArabic ? "معاينة مجلة PDF" : "Magazine PDF preview"} dir={isArabic ? "rtl" : undefined}>
          <button className="mag-pdf-backdrop" type="button" aria-label={isArabic ? "إغلاق المعاينة" : "Close PDF preview"} onClick={() => setSelectedMagazine(null)}></button>
          <div className="mag-pdf-dialog">
            <div className="mag-pdf-head">
              <div>
                <h3>{selectedMagazine.title}</h3>
                {selectedMagazine.subtitle ? <p>{selectedMagazine.subtitle}</p> : null}
              </div>
              <div className="mag-pdf-actions">
                <a href={selectedMagazine.pdf} download target="_blank" rel="noopener noreferrer">
                  {isArabic ? "تحميل ملف PDF" : "Download PDF"}
                </a>
                <button type="button" aria-label={isArabic ? "إغلاق المعاينة" : "Close PDF preview"} onClick={() => setSelectedMagazine(null)}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>
            <iframe src={selectedMagazine.pdf} title={`${selectedMagazine.title} PDF preview`}></iframe>
          </div>
        </div>
      ) : null}

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
